import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_LOGISTICS_API ?? '/api/v1';

export type Shipment = {
  id: string;
  shipment_number: string;
  order_id: string;
  warehouse_id?: string;
  carrier_code?: string;
  status: string;
  tracking_number?: string;
  awb_number?: string;
  origin_pincode: string;
  dest_pincode: string;
  weight: number;
  shipping_cost: number;
  currency: string;
  created_at: string;
};

export type Carrier = {
  id: string;
  code: string;
  name: string;
  active: boolean;
  priority: number;
  sla_days: number;
};

export type RateQuote = {
  carrier_code: string;
  carrier_name: string;
  total_rate: number;
  currency: string;
  estimated_days: number;
  sla_score: number;
};

export type TrackingResponse = {
  shipment_id: string;
  tracking_number: string;
  status: string;
  events: { status: string; description: string; occurred_at: string }[];
};

export type LogisticsDashboard = {
  total_shipments: number;
  in_transit: number;
  delivered: number;
  failed: number;
  pending_pickup: number;
  on_time_rate: number;
  active_carriers: number;
};

export type ServiceabilityResult = {
  carrier_code: string;
  serviceable: boolean;
  cod_available: boolean;
  estimated_days: number;
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...getScopeHeaders(),
      ...init?.headers,
    },
  });
  if (!res.ok) throw new Error(await res.text() || res.statusText);
  if (res.status === 204) return undefined as T;
  const json = await res.json();
  return (json.data ?? json) as T;
}

export const logisticsApi = {
  dashboard: () => request<LogisticsDashboard>('/shipments/dashboard'),

  listShipments: () => request<Shipment[]>('/shipments'),

  createShipment: (body: {
    order_id: string;
    origin_pincode: string;
    dest_pincode: string;
    weight: number;
    length?: number;
    width?: number;
    height?: number;
    carrier_code?: string;
  }) => request<Shipment>('/shipments', { method: 'POST', body: JSON.stringify(body) }),

  assignCarrier: (id: string, carrierCode: string) =>
    request<Shipment>(`/shipments/${id}/assign-carrier`, {
      method: 'POST', body: JSON.stringify({ carrier_code: carrierCode }),
    }),

  generateLabel: (id: string) =>
    request<{ url: string }>(`/shipments/${id}/generate-label`, { method: 'POST', body: '{}' }),

  generateAWB: (id: string) =>
    request<{ awb_number: string }>(`/shipments/${id}/generate-awb`, { method: 'POST', body: '{}' }),

  schedulePickup: (id: string) =>
    request<unknown>(`/shipments/${id}/schedule-pickup`, { method: 'POST', body: '{}' }),

  cancel: (id: string) =>
    request<Shipment>(`/shipments/${id}/cancel`, { method: 'POST', body: '{}' }),

  tracking: (id: string) => request<TrackingResponse>(`/shipments/${id}/tracking`),

  listCarriers: () => request<Carrier[]>('/carriers'),

  rates: (body: { origin_pincode: string; dest_pincode: string; weight: number }) =>
    request<RateQuote[]>('/carriers/rates', { method: 'POST', body: JSON.stringify(body) }),

  serviceability: (body: { origin_pincode: string; dest_pincode: string }) =>
    request<ServiceabilityResult[]>('/carriers/serviceability', { method: 'POST', body: JSON.stringify(body) }),
};
